import React, { createContext, useContext } from 'react';
import { CnIcon } from '@/components/cn-icon';
import { CnButton } from '@/components/cn-button';
import { isValid, clone } from '@formily/shared';
import { useField, useFieldSchema, RecordScope, RecordsScope, } from '@formily/react';
import { usePrefixCls } from '@/form/__builtins__';
import cls from 'classnames';
import './index.scss';
// @ts-ignore
const ArrayBaseContext = createContext(null);
// @ts-ignore
const ItemContext = createContext(null);
const takeRecord = (val) => (typeof val === 'function' ? val() : val);
const useArray = () => {
    return useContext(ArrayBaseContext);
};
const useIndex = (index) => {
    const ctx = useContext(ItemContext);
    return ctx ? ctx.index : index;
};
const useRecord = (record) => {
    const ctx = useContext(ItemContext);
    return takeRecord(ctx ? ctx.record : record);
};
const getSchemaDefaultValue = (schema) => {
    if (schema?.type === 'array')
        return [];
    if (schema?.type === 'object')
        return {};
    if (schema?.type === 'void') {
        for (const key in schema.properties) {
            const value = getSchemaDefaultValue(schema.properties[key]);
            if (isValid(value))
                return value;
        }
    }
};
const getDefaultValue = (defaultValue, schema) => {
    if (isValid(defaultValue))
        return clone(defaultValue);
    if (Array.isArray(schema?.items)) {
        return getSchemaDefaultValue(schema.items[0]);
    }
    return getSchemaDefaultValue(schema.items);
};
export const ArrayBase = (props) => {
    const field = useField();
    const schema = useFieldSchema();
    return (React.createElement(RecordsScope, { getRecords: () => field.value },
        React.createElement(ArrayBaseContext.Provider, { value: { field, schema, props } }, props.children)));
};
ArrayBase.Item = ({ children, ...props }) => {
    return (React.createElement(ItemContext.Provider, { value: props },
        React.createElement(RecordScope, { getIndex: () => props.index, getRecord: () => takeRecord(props.record) }, children)));
};
ArrayBase.Addition = (props) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const self = useField();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const array = useArray();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const schema = useFieldSchema();
    const { maxItems } = schema;
    const maxItemDisabled = maxItems && self.value?.length >= maxItems;
    if (!array)
        return null;
    if (array.field?.pattern !== 'editable' &&
        array.field?.pattern !== 'disabled') {
        return null;
    }
    return (React.createElement(CnButton, { type: "normal", size: "large", fullWidth: true, ...props, disabled: maxItemDisabled || props.disabled, onClick: (e) => {
            if (array.props?.disabled)
                return;
            e.stopPropagation();
            const defaultValue = getDefaultValue(props.defaultValue, array.schema);
            if (props.method === 'unshift') {
                array.field?.unshift?.(defaultValue);
                array.props?.onAdd?.(0);
            }
            else {
                array.field?.push?.(defaultValue);
                let newValue = array?.field?.value;
                // 新增一行时数据置空
                if (newValue?.length) {
                    newValue = [...newValue];
                    newValue[array?.field?.value?.length - 1] = defaultValue;
                    setTimeout(() => {
                        array.field?.setValue(newValue);
                    });
                }
                array.props?.onAdd?.(array?.field?.value?.length - 1);
            }
            if (props.onClick) {
                props.onClick(e);
            }
        } },
        React.createElement(CnIcon, { type: "icon-add", size: "small", className: "table-addition-icon" }),
        props.title || self.title));
};
ArrayBase.Remove = React.forwardRef((props, ref) => {
    const index = useIndex(props.index);
    const self = useField();
    const array = useArray();
    const prefixCls = usePrefixCls('formily-array-base');
    if (!array)
        return null;
    if (array.field?.pattern !== 'editable')
        return null;
    return (React.createElement(CnButton, { text: true, type: "primary", ...props, ref: ref, onClick: (e) => {
            if (self?.disabled)
                return;
            e.stopPropagation();
            array.field?.remove?.(index);
            array.props?.onRemove?.(index);
            if (props.onClick) {
                props.onClick(e);
            }
        } },
        React.createElement(CnIcon
        // size="large"
        , { 
            // size="large"
            type: "icon-delete", className: cls(`${prefixCls}-remove`, self?.disabled ? `${prefixCls}-remove-disabled` : '') })));
});
ArrayBase.Copy = React.forwardRef((props, ref) => {
    const index = useIndex(props.index);
    const self = useField();
    const array = useArray();
    const prefixCls = usePrefixCls('formily-array-base');
    if (!array)
        return null;
    if (array.field?.pattern !== 'editable')
        return null;
    return (React.createElement(CnButton, { type: "primary", text: true, ...props, ref: ref, onClick: (e) => {
            if (self?.disabled)
                return;
            e.stopPropagation();
            const value = clone(array?.field?.value[index]);
            const distIndex = index + 1;
            array.field?.insert?.(distIndex, value);
            array.props?.onCopy?.(distIndex);
            if (props.onClick) {
                props.onClick(e);
            }
        } },
        React.createElement(CnIcon
        // size="large"
        , { 
            // size="large"
            type: "copy", className: cls(`${prefixCls}-copy`, self?.disabled ? `${prefixCls}-copy-disabled` : '') })));
});
ArrayBase.useArray = useArray;
ArrayBase.useIndex = useIndex;
ArrayBase.useRecord = useRecord;
ArrayBase.mixin = (target) => {
    target.Index = ArrayBase.Index;
    target.SortHandle = ArrayBase.SortHandle;
    target.Addition = ArrayBase.Addition;
    target.Copy = ArrayBase.Copy;
    target.Remove = ArrayBase.Remove;
    target.useArray = ArrayBase.useArray;
    target.useIndex = ArrayBase.useIndex;
    target.useRecord = ArrayBase.useRecord;
    return target;
};
